import { Link } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';
import { HighlightedHeading } from '@/components/ui/highlighted-heading';
import { LOCAL_PAGES_IMAGES } from '@/constants/localPagesImages';
import { SmartImage } from '@/components/ui/smart-image';

const TERROIRS = [
  {
    nom: 'Paris intra-muros',
    description: "Mariage de luxe et évènements prestige dans les plus beaux quartiers de la capitale.",
    href: '/wedding-planner-paris',
    image: LOCAL_PAGES_IMAGES.weddingParis.card,
    label: 'Paris (75)',
  },
  {
    nom: 'Seine-et-Marne — Châteaux',
    description: "Les plus beaux châteaux et domaines historiques à moins d'une heure de Paris.",
    href: '/wedding-planner-77-seine-et-marne',
    image: LOCAL_PAGES_IMAGES.weddingSeineEtMarne.card,
    label: 'Seine-et-Marne (77)',
  },
  {
    nom: 'Versailles & Yvelines',
    description: "L'élégance royale du département : Versailles, Saint-Germain, Rambouillet.",
    href: '/agence-evenementielle-versailles-yvelines',
    image: LOCAL_PAGES_IMAGES.versailles.card,
    label: 'Yvelines (78)',
  },
  {
    nom: 'La Défense & Paris Ouest',
    description: "Séminaires d'entreprise et évènements corporate au cœur du quartier d'affaires.",
    href: '/seminaire-paris-la-defense',
    image: LOCAL_PAGES_IMAGES.seminaireLaDefense.card,
    label: 'Hauts-de-Seine (92)',
  },
];

const FORMATS_PHARES = [
  {
    nom: 'Mariage prestige',
    description: 'Wedding planning et wedding design pour des mariages d\'exception sur-mesure.',
    href: '/wedding-planner-paris',
    label: 'Particuliers',
  },
  {
    nom: 'Mariage juif',
    description: 'Houppa, traiteur cacher, animation traditionnelle — orchestré avec respect.',
    href: '/mariage-juif-paris',
    label: 'Particuliers',
  },
  {
    nom: 'Mariage oriental',
    description: 'Maghrébin, libanais, persan, turc — cérémonie du henné et faste oriental.',
    href: '/mariage-oriental-paris',
    label: 'Particuliers',
  },
  {
    nom: 'Mariage afro',
    description: 'Africain, antillais, créole, mixte — cérémonies multiples, traiteur dédié, animation afrobeats.',
    href: '/mariage-afro-paris',
    label: 'Particuliers',
  },
  {
    nom: 'Mariage chinois & asiatique',
    description: 'Chinois, vietnamien, japonais, coréen — cérémonie du thé, banquet 8 plats.',
    href: '/mariage-chinois-paris',
    label: 'Particuliers',
  },
  {
    nom: 'Mariage indien',
    description: 'Hindou, sikh, tamoul, gujarati — Mandap, Baraat, Sangeet, weekend complet.',
    href: '/mariage-indien-paris',
    label: 'Particuliers',
  },
  {
    nom: 'Cérémonie laïque',
    description: "Conception sur-mesure d'une cérémonie d'union émouvante et personnelle.",
    href: '/mariage-laique-paris',
    label: 'Particuliers',
  },
  {
    nom: 'Mariage intime',
    description: 'Elopement, micro-mariage 10-50 invités, renouvellement de vœux. Confidentialité absolue.',
    href: '/mariage-intime-paris',
    label: 'Particuliers',
  },
  {
    nom: 'Demande en mariage',
    description: 'Mise en scène sur-mesure et confidentielle pour un « oui » inoubliable.',
    href: '/proposition-mariage-paris',
    label: 'Particuliers',
  },
  {
    nom: 'Anniversaire prestige',
    description: '30, 40, 50 ans : célébrez votre âge dans un lieu d\'exception parisien.',
    href: '/anniversaire-prestige-paris',
    label: 'Particuliers',
  },
  {
    nom: 'Séminaire & corporate',
    description: 'Évènements d\'entreprise haut de gamme : séminaires, conférences, soirées.',
    href: '/seminaire-paris-la-defense',
    label: 'Entreprises',
  },
];

export const TerroirsSection = () => {
  return (
    <section className="py-16 md:py-24 bg-[#FDFBF7]">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 border border-accent/30 rounded-sm bg-white/60">
            <MapPin className="w-4 h-4 text-accent" />
            <span className="text-[#1a2744]/70 text-xs tracking-[0.2em] uppercase font-medium">
              Île-de-France
            </span>
          </div>
          <HighlightedHeading
            level="h2"
            highlightedText="terroirs"
            className="text-3xl md:text-4xl mb-4"
          >
            Nos terroirs d'intervention
          </HighlightedHeading>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            De Paris intra-muros aux châteaux de Seine-et-Marne, nous orchestrons vos évènements dans toute l'Île-de-France.
          </p>
        </div>

        {/* Grid terroirs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
          {TERROIRS.map((t, i) => (
            <Link
              key={i}
              to={t.href}
              onClick={() => window.scrollTo(0, 0)}
              className="group block border border-border/60 rounded-sm overflow-hidden bg-white hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <div className="aspect-[4/3] md:aspect-auto overflow-hidden bg-muted/30 min-h-[220px]">
                  <SmartImage
                    src={t.image}
                    alt={t.nom}
                    fallbackLabel={t.label}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 md:p-7 flex flex-col justify-center">
                  <span className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2 inline-block">
                    {t.label}
                  </span>
                  <h3 className="font-playfair text-xl md:text-2xl text-[#0a2540] mb-3 group-hover:text-accent transition-colors">
                    {t.nom}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    {t.description}
                  </p>
                  <span className="inline-flex items-center gap-2 text-accent text-sm font-medium group-hover:gap-3 transition-all">
                    Découvrir
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Formats phares */}
        <div className="text-center mb-12">
          <HighlightedHeading
            level="h2"
            highlightedText="formats"
            className="text-3xl md:text-4xl mb-4"
          >
            Tous nos formats d'évènements
          </HighlightedHeading>
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Une expertise étendue pour célébrer chaque moment d'exception, dans la tradition qui vous ressemble.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FORMATS_PHARES.map((f, i) => (
            <Link
              key={i}
              to={f.href}
              onClick={() => window.scrollTo(0, 0)}
              className="group block border-l-2 border-accent bg-white p-6 hover:bg-[#FDFBF7] hover:border-l-4 transition-all duration-300"
            >
              <span className="text-xs uppercase tracking-[0.2em] text-accent/80 font-medium mb-2 inline-block">
                {f.label}
              </span>
              <h3 className="font-playfair text-lg md:text-xl text-[#0a2540] mb-2 group-hover:text-accent transition-colors">
                {f.nom}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                {f.description}
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent text-xs font-medium uppercase tracking-wide">
                En savoir plus
                <ArrowRight className="w-3 h-3" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TerroirsSection;
